import type { Quiz } from "./_schema";
import { quizBeforeTrtQuiz } from "./quiz-before-trt";
import { cortisolQuiz } from "./cortisol";
import { cortisolQuizAlQuiz } from "./cortisol-quiz-al";
import { cortisolQuizGlQuiz } from "./cortisol-quiz-gl";
import { cortisolQuizXQuiz } from "./cortisol-quiz-x";
import { cortisolStoryQuiz } from "./cortisol-story";
import { cortisolQuizEgXQuiz } from "./cortisol-quiz-eg-x";
import { cortisolWeightlossQuiz } from "./cortisol-weightloss";
import { boostQuiz } from "./boost";
import { quizV6GatedQuiz } from "./quiz-v6-gated";
import { quizV6EgXQuiz } from "./quiz-v6-eg-x";
import { quizGlpQuiz } from "./quiz-glp";
import { quizV6GlQuiz } from "./quiz-v6-gl";
import { quizV6AlQuiz } from "./quiz-v6-al";
import { quizV6XQuiz } from "./quiz-v6-x";
import { quizV6EsQuiz } from "./quiz-v6-es";
import { quizV7Quiz } from "./quiz-v7";
import { tCalcQuiz } from "./t-calc";
import { quizTrtSwitchQuiz } from "./quiz-trt-switch";
import { quizWeightlossQuiz } from "./quiz-weightloss";
import { quizWeightlossV2Quiz } from "./quiz-weightloss-v2";
import { quizEnergyQuiz } from "./quiz-energy";
import { quizSleepQuiz } from "./quiz-sleep";
import { quizBeerQuiz } from "./quiz-beer";
import { quizLibidoQuiz } from "./quiz-libido";
import { quizFocusQuiz } from "./quiz-focus";
import { boostAgeStory } from "./boost-age-story";
import { boostEnergyStory } from "./boost-energy-story";
import { boostWeightStory } from "./boost-weight-story";

// `free-t` is intentionally NOT registered as a public slug. The free-t quiz
// implementation in `quizzes/free-t.ts` is the canonical Vermeulen calculator
// that `t-calc` inherits from (`tCalcQuiz.compute = freeTQuiz.compute`, etc.),
// but it has no live Shopify page and is not loadable via `?quiz=free-t`. If
// a future calculator wants to expose it as its own slug, add the entry back
// here and to `api/schemas/submission.py` (QUIZ_SLUGS, plus CALCULATOR_SLUGS or
// DIAGNOSTIC_SLUGS for a kind that submits a null persona).
export const quizRegistry: Record<string, Quiz> = {
  [boostQuiz.slug]: boostQuiz,
  [quizV6GatedQuiz.slug]: quizV6GatedQuiz,
  [quizV6EgXQuiz.slug]: quizV6EgXQuiz,
  [quizV6GlQuiz.slug]: quizV6GlQuiz,
  [quizV6AlQuiz.slug]: quizV6AlQuiz,
  [quizV6XQuiz.slug]: quizV6XQuiz,
  [quizV6EsQuiz.slug]: quizV6EsQuiz,
  [quizV7Quiz.slug]: quizV7Quiz,
  [tCalcQuiz.slug]: tCalcQuiz,
  [cortisolQuiz.slug]: cortisolQuiz,
  [cortisolQuizAlQuiz.slug]: cortisolQuizAlQuiz,
  [cortisolQuizGlQuiz.slug]: cortisolQuizGlQuiz,
  [cortisolQuizXQuiz.slug]: cortisolQuizXQuiz,
  [cortisolStoryQuiz.slug]: cortisolStoryQuiz,
  [cortisolQuizEgXQuiz.slug]: cortisolQuizEgXQuiz,
  [cortisolWeightlossQuiz.slug]: cortisolWeightlossQuiz,
  [quizGlpQuiz.slug]: quizGlpQuiz,
  [quizBeforeTrtQuiz.slug]: quizBeforeTrtQuiz,
  [quizTrtSwitchQuiz.slug]: quizTrtSwitchQuiz,
  [quizWeightlossQuiz.slug]: quizWeightlossQuiz,
  [quizWeightlossV2Quiz.slug]: quizWeightlossV2Quiz,
  [quizEnergyQuiz.slug]: quizEnergyQuiz,
  [quizSleepQuiz.slug]: quizSleepQuiz,
  [quizBeerQuiz.slug]: quizBeerQuiz,
  [quizLibidoQuiz.slug]: quizLibidoQuiz,
  [quizFocusQuiz.slug]: quizFocusQuiz,
  [boostAgeStory.slug]: boostAgeStory,
  [boostEnergyStory.slug]: boostEnergyStory,
  [boostWeightStory.slug]: boostWeightStory,
};

/**
 * Slugs a quiz used to answer to, mapped to what it is called now.
 *
 * A rename is never atomic across the three places a slug lives. The Shopify
 * page carries the old name in its `data-mars-quiz` attribute until someone
 * republishes it; the widget bundle is pointed at by `manifest.json` with a
 * 60s TTL, so for a few minutes after a deploy some visitors are still running
 * the PREVIOUS bundle, which knows only the old name; and `quizzes.json` is a
 * public manifest that downstream embeds (the LP generator) may have copied a
 * slug out of. Without an alias, every one of those renders "Quiz not found"
 * on live ad traffic.
 *
 * This map is deliberately NOT merged into `quizRegistry`. Everything that
 * ENUMERATES the registry -- the admin quiz list, `npm run validate-quiz`, the
 * rollup manifest generator, the smoke page list -- must see each quiz exactly
 * once, so a retired name has to resolve on LOOKUP without existing as a quiz.
 *
 * An entry costs nothing to keep and is the only thing standing between a
 * stale cache and a blank screen, so leave it. `tests/quizzes/boost.test.ts`
 * locks this one; `tests/regression/frontend-backend-contract.test.ts` reads
 * this map so the API is allowed to accept a retired slug (and only a retired
 * one) without tripping its accepts-nothing-unregistered check.
 */
export const RETIRED_SLUG_ALIASES: Record<string, string> = {
  // `quiz-v6` was called `ignite` from launch until 2026-09-17. The name came
  // from the Heyflow era and collided with Ignite the cognition stick pack
  // once `quiz-focus` shipped. The page handle never changed: /pages/quiz-v6.
  ignite: boostQuiz.slug,
};

/**
 * Translate a slug that arrived from OUTSIDE the bundle -- a `data-mars-quiz`
 * attribute, a `?quiz=` param -- into the name the registry uses today.
 *
 * Deliberately the only place a retired name is honoured, and it sits at the
 * web-component boundary (`web/src/main.tsx`) rather than inside `MarsMenQuiz`.
 * Everything past that boundary then deals exclusively in canonical slugs, so
 * there is no second lookup path to keep in step -- and, concretely, the alias
 * cannot leak into what gets RECORDED: `MarsMenQuiz` reads `quiz.slug` for
 * every submission and event, so a visitor arriving through the old mount
 * point is filed under the new name instead of splitting the funnel in two.
 *
 * An unknown slug is returned unchanged, so the caller still renders its own
 * "Quiz not found" rather than this silently inventing a quiz.
 */
export function canonicalSlug(slug: string): string {
  return RETIRED_SLUG_ALIASES[slug] ?? slug;
}
